import { useQuery, keepPreviousData } from "@tanstack/react-query";
import { researchApi, EnrichedTrackEntity } from "../../services/researchApi";
import { ResearchEntity } from "../../types/researchWorkflow";
import { queryKeys } from "./keys";

function transformEntity(backendEntity: EnrichedTrackEntity): ResearchEntity {
  // Discovery score (for long list): prefer the direct match_score — it
  // reflects the server-side live inactive clamp — with raw_llm_response
  // parsing as the legacy fallback.
  let discoveryScore: number | undefined;
  const directScore = backendEntity.latest_reference?.discovery_meta?.match_score;
  if (typeof directScore === "number") {
    discoveryScore = Math.round(directScore * 100);
  } else {
    try {
      const rawResponse =
        backendEntity.latest_reference?.discovery_meta?.raw_llm_response;
      if (rawResponse && typeof rawResponse === "string") {
        const parsed = JSON.parse(rawResponse);
        if (parsed.match_score !== undefined) {
          discoveryScore = Math.round(parsed.match_score * 100);
        }
      }
    } catch (error) {
      console.warn("Failed to parse raw_llm_response:", error);
    }
  }

  return {
    id: backendEntity.id,
    kg_entity_id: backendEntity.kg_entity_id,
    name:
      backendEntity.kg_data?.name ||
      backendEntity.metadata?.name ||
      backendEntity.metadata?.domain ||
      (backendEntity.kg_entity_id.startsWith("domain:")
        ? backendEntity.kg_entity_id.slice("domain:".length)
        : `Entity ${backendEntity.kg_entity_id}`),
    type: backendEntity.kg_entity_type,
    description: backendEntity.kg_data?.description,
    inactive: backendEntity.inactive ?? false,

    // Domain candidates have no kg_data; the web lane / profile snapshot
    // stores the extracted ISO2 in metadata instead.
    home_country:
      backendEntity.kg_data?.home_country ||
      backendEntity.metadata?.country_iso2,
    country_iso2: backendEntity.kg_data?.country_iso2,
    founding_year: backendEntity.kg_data?.founding_year,
    facility_type: backendEntity.kg_data?.facility_type,
    project_type: backendEntity.kg_data?.project_types,
    tags: backendEntity.kg_data?.labels || [],

    // Discovery score (always from discovery phase, shown in long list)
    relevanceScore: discoveryScore,
    matchCategory: backendEntity.latest_reference?.discovery_meta
      ?.match_category as ResearchEntity["matchCategory"],
    discoverySource:
      backendEntity.latest_reference?.discovery_meta?.discovery_source,
    evaluationReasoning:
      backendEntity.latest_reference?.discovery_meta?.reasoning ||
      backendEntity.latest_reference?.discovery_meta?.evaluation_reasoning,
    // Deep evaluation data (from Output table, shown in short list)
    evaluationData: backendEntity.evaluationData || undefined,
    // Latest reference (includes discovery_meta for tooltip)
    latest_reference: backendEntity.latest_reference || undefined,
    specVersionId: backendEntity.latest_reference?.spec_version_id,
    specVersionNumber: backendEntity.latest_reference?.spec_version_number,
    workflowStatus: (backendEntity.latest_decision?.decision_state ||
      "candidate") as any,
    rejectionReason: backendEntity.latest_decision?.decision_meta?.reason,
    deepResearchStatus: (backendEntity.deepResearchStatus ||
      "not_started") as any,
    // Per-candidate evaluation execution status (#888)
    evaluationStatus: backendEntity.evaluationStatus || "none",
    evaluationTaskId: backendEntity.evaluationTaskId,
    evaluationRunId: backendEntity.evaluationRunId,
    evaluationRunAt: backendEntity.evaluationRunAt,
    documentCount: backendEntity.documentCount ?? 0,

    addedToWorkflowDate: backendEntity.first_discovered_at,
    lastUpdated:
      backendEntity.latest_decision?.decided_at ||
      backendEntity.first_discovered_at,
  };
}

const POLL_INTERVAL_IDLE = 30_000;
const POLL_INTERVAL_RUNNING = 5_000;

export function useTrackEntities(trackId: string | null, isRunning: boolean) {
  return useQuery({
    queryKey: queryKeys.research.trackEntities(trackId!),
    queryFn: async () => {
      if (!trackId) return [];

      const response = await researchApi.getEnrichedEntities(trackId);
      const entities = response.entities.map(transformEntity);

      return entities;
    },
    enabled: !!trackId,
    refetchInterval: isRunning ? POLL_INTERVAL_RUNNING : POLL_INTERVAL_IDLE,
    placeholderData: keepPreviousData,
  });
}
